import { formatDate, formatVND, PAYMENT_TYPE_OPTIONS } from "../../../constants"
import ApprovalRouteCard from "../ApprovalRouteCard"
import { useGetPaymentRequestApprovalPreviewQuery } from "shared/redux/endpoints/procurementPayments"
import GrStatusBadge from "./GrStatusBadge"
import NonPoBudgetCheck from "./NonPoBudgetCheck"
import PaymentSummary from "./PaymentSummary"
import { HScroll } from "procurement/design/components/HScroll"

const typeLabel = (v) => PAYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v || "—"

const Preview = ({ params, skip, title }) => {
  const { data, isFetching } = useGetPaymentRequestApprovalPreviewQuery(params, { skip })
  return (
    <div style={{ marginTop: title ? 8 : 0 }}>
      {title && <div className="prc-label">{title}</div>}
      <ApprovalRouteCard approvalPreview={data} loading={isFetching} />
    </div>
  )
}

const fileName = (file) => file?.name || null

// Read-only check before submit. Edit sends the user back to the form.
const PaymentRequestReview = ({
  requestType,
  rows,
  shared,
  invoice,
  directTitle,
  directLines,
  directTotal,
  additionalDocs,
  totals,
  onEdit,
  onBudgetStatus,
}) => {
  const isPo = requestType === "po"
  const suppliers = [...new Set((rows || []).map((r) => r.supplierName).filter(Boolean))]
  return (
    <div>
      <section className="prc-card">
        <header className="prc-card-h">
          <div>
            <h2>Payment Request Review</h2>
            <p>Check the type, invoices, amounts, and attachments before submitting.</p>
          </div>
          <div className="prc-card-actions">
            <button type="button" className="prc-linkish" onClick={onEdit} style={{ fontSize: 13 }}>
              Edit
            </button>
          </div>
        </header>
        <div className="prc-card-b">
          <div className="prc-kvs">
            <div>
              <span>Payment request type</span>
              <b>
                {isPo
                  ? "Link to Purchase Order(s)"
                  : requestType === "direct"
                  ? "Non-PO Payment"
                  : "Payment without a Purchase Order"}
              </b>
            </div>
            <div>
              <span>Payment type</span>
              <b>{typeLabel(shared.paymentType)}</b>
            </div>
            <div>
              <span>Planned payment date</span>
              <b>{shared.paymentTimeline ? formatDate(shared.paymentTimeline) : "—"}</b>
            </div>
            {isPo && (
              <div>
                <span>Supplier(s)</span>
                <b>{suppliers.join(", ") || "—"}</b>
              </div>
            )}
            {directTitle && (
              <div>
                <span>Title</span>
                <b>{directTitle}</b>
              </div>
            )}
            <div>
              <span>Payee</span>
              <b>{shared.bankReceiverName || "—"}</b>
            </div>
            <div>
              <span>Bank</span>
              <b>{[shared.bankNameBranch, shared.bankAccount].filter(Boolean).join(" · ") || "—"}</b>
            </div>
          </div>
          {isPo && rows.length > 1 && (
            <p className="prc-help">All selected purchase orders are combined into one payment request with one invoice.</p>
          )}
        </div>
      </section>

      {isPo && (
        <section className="prc-card">
          <header className="prc-card-h">
            <div>
              <h2>Selected purchase orders</h2>
              <p>{rows.length} PO{rows.length === 1 ? "" : "s"}</p>
            </div>
          </header>
          <div className="prc-card-b prc-flush">
            <HScroll className="prc-table-wrap">
              <table className="prc-table prc-review-table">
                <thead>
                  <tr>
                    <th>PO Number</th>
                    <th>Supplier</th>
                    <th>Goods Receipt</th>
                    <th className="prc-num">Payment</th>
                    <th className="prc-num">%</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.purchaseId}>
                      <td className="prc-po">{row.poCode || `PO #${row.purchaseId}`}</td>
                      <td>{row.supplierName || "—"}</td>
                      <td>
                        <GrStatusBadge purchaseId={row.purchaseId} />
                      </td>
                      <td className="prc-num">{formatVND(row.amount)}</td>
                      <td className="prc-num">{row.percent === "" || row.percent == null ? "—" : `${row.percent}%`}</td>
                      <td className="prc-note-full">{row.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </HScroll>
          </div>
        </section>
      )}

      {!isPo && directLines?.length > 0 && (
        <section className="prc-card">
          <header className="prc-card-h">
            <div>
              <h2>Line items</h2>
            </div>
          </header>
          <div className="prc-card-b">
            {directLines.map((line) => (
              <div key={line.key} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0" }}>
                <span>{(line.isCustomItem ? line.customItemName : line.itemName) || "Item"}</span>
                <b>{formatVND(Number(line.amount) || 0)}</b>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="prc-card">
        <header className="prc-card-h">
          <div>
            <h2>Invoice</h2>
          </div>
        </header>
        <div className="prc-card-b">
          <div className="prc-kvs">
            <div>
              <span>Serial</span>
              <b>{invoice.invoiceSerial || "—"}</b>
            </div>
            <div>
              <span>Number</span>
              <b>{invoice.invoiceNumber || "—"}</b>
            </div>
            <div>
              <span>Date</span>
              <b>{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : "—"}</b>
            </div>
            <div>
              <span>Tax code</span>
              <b>{invoice.supplierTaxCode || "—"}</b>
            </div>
            {invoice.invoiceAmount != null && (
              <div>
                <span>Invoice amount</span>
                <b>{formatVND(invoice.invoiceAmount)}</b>
              </div>
            )}
            {!isPo && (
              <div>
                <span>Note</span>
                <b className="prc-note-full">{invoice.note || "—"}</b>
              </div>
            )}
            <div>
              <span>Invoice files</span>
              <b>{[fileName(invoice.xmlFile), fileName(invoice.pdfFile)].filter(Boolean).join(", ") || "None"}</b>
            </div>
          </div>
        </div>
      </section>

      <section className="prc-card">
        <header className="prc-card-h">
          <div>
            <h2>Additional documents</h2>
            <p>Attached to the payment request, separate from the invoice PDF.</p>
          </div>
        </header>
        <div className="prc-card-b">
          {additionalDocs.length === 0 ? (
            <span className="prc-sub">No supporting documents.</span>
          ) : (
            additionalDocs.map((file, i) => (
              <div key={`${file.name}-${i}`}>{file.name}</div>
            ))
          )}
          {shared.description && (
            <div style={{ marginTop: 12 }}>
              <span className="prc-label">General note</span>
              <div className="prc-note-full">{shared.description}</div>
            </div>
          )}
        </div>
      </section>

      <section className="prc-card">
        <header className="prc-card-h">
          <div>
            <h2>Payment Request Summary</h2>
          </div>
        </header>
        <div className="prc-card-b">
          {isPo ? (
            <PaymentSummary {...totals} />
          ) : (
            <PaymentSummary singleAmount={requestType === "direct" ? directTotal : invoice.amount} />
          )}
        </div>
      </section>

      {!isPo && requestType === "direct" && (
        <section className="prc-card">
          <header className="prc-card-h">
            <div>
              <h2>Budget check</h2>
              <p>The payment total is committed against the category budget when you submit.</p>
            </div>
          </header>
          <div className="prc-card-b">
            <NonPoBudgetCheck lines={directLines || []} categoryId={shared.previewCategory} onStatus={onBudgetStatus} />
          </div>
        </section>
      )}

      <section className="prc-card">
        <header className="prc-card-h">
          <div>
            <h2>Approval route</h2>
          </div>
        </header>
        <div className="prc-card-b">
          {isPo &&
            rows.map((row) => (
              <Preview
                key={row.purchaseId}
                title={rows.length > 1 ? row.poCode || `PO #${row.purchaseId}` : undefined}
                params={{
                  category: row.budgetCategoryId,
                  amount: Number(row.amount) || 0,
                  region: row.region,
                  center: row.center,
                }}
                skip={!row.budgetCategoryId || !(Number(row.amount) > 0)}
              />
            ))}
          {!isPo && requestType === "direct" && (
            <Preview
              params={{
                category: shared.previewCategory,
                amount: Number(directTotal) || 0,
                region: shared.previewRegion,
                center: shared.previewCenter,
              }}
              skip={!shared.previewCategory || !(Number(directTotal) > 0)}
            />
          )}
          {!isPo && requestType !== "direct" && (
            <Preview params={{ amount: Number(invoice.amount) || 0 }} skip={!(Number(invoice.amount) > 0)} />
          )}
        </div>
      </section>
    </div>
  )
}

export default PaymentRequestReview
